/**
 * InstanceLayoutController - Manages the instance layout modal
 * 
 * Handles group positioning (left/right/top/bottom), splitting, coloring,
 * port order (clockwise/counterclockwise), and folding/unfolding of port groups.
 * Emits DEVICE_UPDATED when layout changes are saved.
 */

import { escapeHtml } from '../utils/index.js';
import { deepClone } from '../utils/helpers.js';
import { eventBus, EVENTS } from '../services/EventBus.js';
import { showToast } from './ToastController.js';
import { MODAL_IDS } from '../constants/modals.js';
import { CONNECTOR_TYPES } from '../constants/index.js';
import { normalizeSignalTypes } from '../services/ConnectionService.js';

// ============= MODULE STATE =============
let layoutInstanceId = null;
let layoutOverrides = {}; // { groupId: "left" | "right" | { side, splitSize, color, folded } }
let layoutPortOrder = 'default'; // 'default' or 'clockwise'
let layoutDisplayName = ''; // Custom display name for this instance

// Color palette for group customization
const colorPalette = [
    null,       // Reset/default
    '#00d9ff', '#00ff88', '#ff9f1c', '#a855f7', '#ff4444',
    '#22c55e', '#f59e0b', '#ec4899', '#6366f1', '#14b8a6',
    '#84cc16', '#f97316', '#8b5cf6', '#06b6d4', '#eab308'
];

// Default colors for each signal type
const defaultTypeColors = {
    audio: '#00d9ff',
    midi: '#ff9f1c',
    'midi-rtp': '#f59e0b',
    'midi-usb-host': '#eab308',
    'midi-usb-device': '#facc15',
    cv: '#a855f7',
    gate: '#22c55e',
    clock: '#f472b6'
    // Note: 'insert' is a port direction, not a signal type
};

// Dependencies (injected via init)
let instanceModel = null;
let definitionModel = null;
let renderDeviceFn = null;
let renderAllCablesFn = null;
let updateConnectedPortsFn = null;
let recordActionFn = null;
let scheduleAutosaveFn = null;
let getTemplateForDeviceFn = null;
let getGroupDisplayNameFn = null;

// DOM reference
let layoutModal = null;

/**
 * Initialize the InstanceLayoutController
 * @param {Object} deps - Dependencies
 * @returns {Object} Public methods for onclick handlers
 */
export function initInstanceLayoutController(deps) {
    // Store dependencies
    instanceModel = deps.instanceModel;
    definitionModel = deps.definitionModel;
    renderDeviceFn = deps.renderDevice;
    renderAllCablesFn = deps.renderAllCables;
    updateConnectedPortsFn = deps.updateConnectedPorts;
    recordActionFn = deps.recordAction;
    scheduleAutosaveFn = deps.scheduleAutosave;
    getTemplateForDeviceFn = deps.getTemplateForDevice;
    getGroupDisplayNameFn = deps.getGroupDisplayName;
    
    // Get DOM reference
    layoutModal = document.getElementById(MODAL_IDS.INSTANCE_LAYOUT);
    
    // Return public methods for window/onclick exposure
    return {
        showInstanceLayoutModal,
        renderLayoutGroups,
        setGroupSide,
        setGroupSplit,
        setGroupColor,
        toggleColorPicker,
        closeLayoutModal,
        closeInstanceLayoutModal: closeLayoutModal, // alias
        resetInstanceLayout,
        saveInstanceLayout,
        toggleClockwisePortOrder,
        unfoldGroup,
        foldGroupInline,
        setDeviceGroupSide,
        isGroupFoldedInDevice
    };
}

/**
 * Show the instance layout modal for a device
 * @param {Object} device - Device instance
 */
function showInstanceLayoutModal(device) {
    const template = getTemplateForDeviceFn(device);
    if (!template) return;
    
    layoutInstanceId = device.id;
    // Clone existing layout overrides or start fresh
    layoutOverrides = device.groupLayout ? {...device.groupLayout} : {};
    // Load port order setting
    layoutPortOrder = device.portOrder || 'default';
    // Load display name
    layoutDisplayName = device.displayName || '';
    
    // Migrate any legacy UUID keys to index keys
    // This ensures all overrides are stored under unique index keys
    if (template.portGroups) {
        template.portGroups.forEach((group, groupIndex) => {
            const indexKey = String(groupIndex);
            // If there's a UUID-based override but no index-based override, migrate it
            if (group.id && layoutOverrides[group.id] !== undefined && layoutOverrides[indexKey] === undefined) {
                layoutOverrides[indexKey] = layoutOverrides[group.id];
                delete layoutOverrides[group.id];
            }
        });
    }
    
    document.getElementById('layout-instance-id').value = device.id;
    
    // Set display name input
    const displayNameInput = document.getElementById('layout-display-name');
    if (displayNameInput) {
        displayNameInput.value = layoutDisplayName;
    }
    
    // Set clockwise toggle checkbox
    const clockwiseToggle = document.getElementById('layout-clockwise-toggle');
    if (clockwiseToggle) {
        clockwiseToggle.checked = layoutPortOrder === 'clockwise';
    }
    
    // Update modal title with device name
    const modalTitle = layoutModal.querySelector('h2');
    let deviceTitle = template.name;
    if (template.manufacturer) {
        deviceTitle = `${template.manufacturer} ${template.name}`;
    }
    modalTitle.textContent = `Instance Layout: ${deviceTitle}`;
    
    renderLayoutGroups(template);
    layoutModal.classList.add('active');
}

/**
 * Toggle clockwise port order
 * @param {boolean} checked - Whether clockwise is enabled
 */
function toggleClockwisePortOrder(checked) {
    layoutPortOrder = checked ? 'clockwise' : 'default';
}

/**
 * Render the layout groups in the modal
 * @param {Object} template - Device template
 */
function renderLayoutGroups(template) {
    const container = document.getElementById('layout-groups-container');
    
    if (!template.portGroups || template.portGroups.length === 0) {
        container.innerHTML = '<div class="empty-groups">No port groups defined.</div>';
        return;
    }
    
    container.innerHTML = template.portGroups.map((group, groupIndex) => {
        // ALWAYS use numeric index as key - this ensures each group has a unique identifier
        // even if multiple groups somehow have the same group.id (which could happen through
        // data corruption, bad imports, or copy/paste issues)
        const groupKey = String(groupIndex);
        
        // Read override from index key first, fall back to legacy UUID key for backwards compat
        const override = layoutOverrides[groupKey] || (group.id && layoutOverrides[group.id]) || {};
        const defaultSide = group.direction === 'input' ? 'left' : 'right';
        const currentSide = (typeof override === 'string') ? override : (override.side || defaultSide);
        const currentSplit = (typeof override === 'object') ? override.splitSize : null;
        const currentColor = (typeof override === 'object') ? override.color : null;
        const isOverridden = currentSide !== defaultSide || currentSplit || currentColor;
        
        const sideClass = (side) => {
            if (currentSide !== side) return '';
            return 'active' + (isOverridden ? ' overridden' : '');
        };
        
        const splitPreview = currentSplit && group.count > currentSplit
            ? `<span class="split-preview">→ ${Math.ceil(group.count / currentSplit)} sub-groups</span>`
            : '';
        
        // Color swatch display
        const groupTypes = normalizeSignalTypes(group.type);
        const primaryType = groupTypes[0];
        const displayColor = currentColor || defaultTypeColors[primaryType] || '#888';
        const connectorLabel = group.connector ? CONNECTOR_TYPES[group.connector]?.label : null;
        const typeLabel = groupTypes.length > 1 ? groupTypes.join('/') : primaryType;
        
        return `
            <div class="layout-group-row" data-group-id="${groupKey}">
                <div class="group-info">
                    <div class="group-name">${escapeHtml(getGroupDisplayNameFn(group))}</div>
                    <div class="group-meta">
                        <span class="group-type-badge ${primaryType}">${typeLabel}</span>
                        ${connectorLabel ? `<span style="opacity:0.7">[${connectorLabel}]</span>` : ''}
                        ${group.direction} • ${group.count} port${group.count > 1 ? 's' : ''}
                        • default: ${defaultSide}
                        ${splitPreview}
                    </div>
                </div>
                <div class="color-picker-wrap">
                    <div class="color-swatch ${currentColor ? 'active' : ''}" 
                         style="background: ${displayColor};"
                         onclick="toggleColorPicker(this, '${groupKey}', '${group.type}')"
                         title="Click to change color">
                    </div>
                </div>
                <div class="port-group-compact" style="margin-right: 8px;">
                    <label style="font-size: 0.7rem; color: #666;">Split</label>
                    <input type="number" class="small" value="${currentSplit || ''}" min="1" max="64" placeholder="—" 
                           onchange="setGroupSplit('${groupKey}', this.value)" 
                           title="Split into sub-groups of this size" 
                           style="width: 45px;">
                </div>
                <div class="side-toggle">
                    <button class="side-btn ${sideClass('top')}" onclick="setGroupSide('${groupKey}', 'top', '${defaultSide}')" title="Top (horizontal)">▲</button>
                    <button class="side-btn ${sideClass('left')}" onclick="setGroupSide('${groupKey}', 'left', '${defaultSide}')" title="Left (vertical)">◀</button>
                    <button class="side-btn ${sideClass('right')}" onclick="setGroupSide('${groupKey}', 'right', '${defaultSide}')" title="Right (vertical)">▶</button>
                    <button class="side-btn ${sideClass('bottom')}" onclick="setGroupSide('${groupKey}', 'bottom', '${defaultSide}')" title="Bottom (horizontal)">▼</button>
                </div>
            </div>
        `;
    }).join('');
}

/**
 * Toggle color picker popup for a group
 * @param {HTMLElement} swatchEl - The color swatch element clicked
 * @param {string} groupId - Group identifier (index)
 * @param {string} groupType - Group signal type
 */
function toggleColorPicker(swatchEl, groupId, groupType) {
    // Remove any existing color picker
    document.querySelectorAll('.color-palette-popup').forEach(p => p.remove());
    
    // Create color palette popup
    const popup = document.createElement('div');
    popup.className = 'color-palette-popup';
    popup.style.cssText = `
        position: absolute;
        background: #1e2d4d;
        border: 1px solid #3a5a8f;
        border-radius: 6px;
        padding: 8px;
        z-index: 1001;
        box-shadow: 0 4px 12px rgba(0,0,0,0.4);
    `;
    
    const existing = layoutOverrides[groupId] || {};
    const currentColor = (typeof existing === 'object') ? existing.color : null;
    
    popup.innerHTML = `
        <div class="color-palette">
            ${colorPalette.map((color, idx) => {
                if (color === null) {
                    return `<div class="color-option color-reset ${!currentColor ? 'selected' : ''}" 
                                onclick="setGroupColor('${groupId}', null)" 
                                title="Reset to default"></div>`;
                }
                return `<div class="color-option ${currentColor === color ? 'selected' : ''}" 
                            style="background: ${color};"
                            onclick="setGroupColor('${groupId}', '${color}')"
                            title="${color}"></div>`;
            }).join('')}
        </div>
    `;
    
    // Position popup below swatch
    const rect = swatchEl.getBoundingClientRect();
    const modalRect = document.querySelector('#instance-layout-modal .modal').getBoundingClientRect();
    popup.style.left = (rect.left - modalRect.left) + 'px';
    popup.style.top = (rect.bottom - modalRect.top + 4) + 'px';
    
    document.querySelector('#instance-layout-modal .modal').appendChild(popup);
    
    // Close on click outside
    const closeHandler = (e) => {
        if (!popup.contains(e.target) && e.target !== swatchEl) {
            popup.remove();
            document.removeEventListener('click', closeHandler);
        }
    };
    setTimeout(() => document.addEventListener('click', closeHandler), 0);
}

/**
 * Set the color for a group
 * @param {string} groupId - Group identifier (index)
 * @param {string|null} color - Color hex string or null for default
 */
function setGroupColor(groupId, color) {
    const existing = layoutOverrides[groupId] || {};
    
    if (typeof existing === 'string') {
        // Was just a side string, convert to object
        if (color) {
            layoutOverrides[groupId] = { side: existing, color: color };
        } else {
            layoutOverrides[groupId] = existing; // Keep as string if no color
        }
    } else {
        if (color) {
            layoutOverrides[groupId] = { ...existing, color: color };
        } else {
            delete layoutOverrides[groupId].color;
        }
    }
    
    // Clean up empty override
    cleanupLayoutOverride(groupId);
    
    // Close popup
    document.querySelectorAll('.color-palette-popup').forEach(p => p.remove());
    
    // Re-render
    const device = instanceModel.getById(layoutInstanceId);
    if (device) {
        const template = getTemplateForDeviceFn(device);
        if (template) {
            renderLayoutGroups(template);
        }
    }
}

/**
 * Clean up empty layout override
 * @param {string} groupId - Group identifier
 */
function cleanupLayoutOverride(groupId) {
    const override = layoutOverrides[groupId];
    if (!override) return;
    
    if (typeof override === 'object') {
        // Remove if only has default values
        const hasContent = override.side || override.splitSize || override.color || override.folded;
        if (!hasContent) {
            delete layoutOverrides[groupId];
        }
    }
}

/**
 * Unfold a group by clicking on the folded connector
 * @param {string} deviceId - Device ID
 * @param {string} groupId - Group identifier (index or fold key like "0:1")
 */
function unfoldGroup(deviceId, groupId) {
    const device = instanceModel.getById(deviceId);
    if (!device) return;
    
    // Initialize groupLayout if needed
    if (!device.groupLayout) {
        device.groupLayout = {};
    }
    
    const existing = device.groupLayout[groupId] || {};
    
    if (typeof existing === 'string') {
        // Was just a side string, nothing to unfold
        return;
    }
    
    // Remove folded flag
    delete existing.folded;
    
    // Clean up if empty
    const hasContent = existing.side || existing.splitSize || existing.color;
    if (hasContent) {
        device.groupLayout[groupId] = existing;
    } else {
        delete device.groupLayout[groupId];
    }
    
    // Clean up empty groupLayout
    if (Object.keys(device.groupLayout).length === 0) {
        delete device.groupLayout;
    }
    
    // Re-render the device
    renderDeviceFn(device);
    renderAllCablesFn();
    updateConnectedPortsFn();
    scheduleAutosaveFn();
    
    // Emit device updated event
    eventBus.emit(EVENTS.DEVICE_UPDATED, { deviceId });
}

/**
 * Fold a group by clicking the inline toggle (opposite of unfoldGroup)
 * @param {string} deviceId - Device ID
 * @param {string} groupId - Group identifier
 * @param {Event} event - Click event
 */
function foldGroupInline(deviceId, groupId, event) {
    // Prevent click from propagating to device selection
    if (event) {
        event.stopPropagation();
    }
    
    const device = instanceModel.getById(deviceId);
    if (!device) return;
    
    // Initialize groupLayout if needed
    if (!device.groupLayout) {
        device.groupLayout = {};
    }
    
    const existing = device.groupLayout[groupId] || {};
    
    if (typeof existing === 'string') {
        // Was just a side string, convert to object with folded
        device.groupLayout[groupId] = { side: existing, folded: true };
    } else {
        // Add folded flag
        device.groupLayout[groupId] = { ...existing, folded: true };
    }
    
    // Re-render the device
    renderDeviceFn(device);
    renderAllCablesFn();
    updateConnectedPortsFn();
    scheduleAutosaveFn();
    
    // Emit device updated event
    eventBus.emit(EVENTS.DEVICE_UPDATED, { deviceId });
}

/**
 * Change a group's side for a device instance (used by Alt+WASD shortcut)
 * @param {string} deviceId - Device ID
 * @param {string} groupId - Group identifier (string index or fold key)
 * @param {string} newSide - New side ('left', 'right', 'top', 'bottom')
 * @returns {boolean} Success
 */
function setDeviceGroupSide(deviceId, groupId, newSide) {
    const device = instanceModel.getById(deviceId);
    if (!device) return false;
    
    // Initialize groupLayout if needed
    if (!device.groupLayout) {
        device.groupLayout = {};
    }
    
    const existing = device.groupLayout[groupId] || {};
    
    if (typeof existing === 'string') {
        // Was just a side string, update it
        device.groupLayout[groupId] = newSide;
    } else {
        // Object with other properties, update side
        device.groupLayout[groupId] = { ...existing, side: newSide };
    }
    
    // Re-render the device
    renderDeviceFn(device);
    renderAllCablesFn();
    updateConnectedPortsFn();
    scheduleAutosaveFn();
    
    // Emit device updated event
    eventBus.emit(EVENTS.DEVICE_UPDATED, { deviceId });
    
    return true;
}

/**
 * Check if a group (or any of its subgroups) is folded in a device
 * @param {Object} device - Device instance
 * @param {string} groupId - Group identifier
 * @param {number|null} portIndex - Optional port index for subgroup check
 * @returns {boolean} Whether the group is folded
 */
function isGroupFoldedInDevice(device, groupId, portIndex = null) {
    const layout = device.groupLayout;
    if (!layout) return false;
    
    // Check direct group fold (non-split groups)
    if (typeof layout[groupId] === 'object' && layout[groupId].folded) {
        return true;
    }
    
    // For subgroups, check if the specific subgroup containing portIndex is folded
    if (portIndex !== null) {
        const splitSize = typeof layout[groupId] === 'object' ? layout[groupId].splitSize : null;
        if (splitSize) {
            // Calculate which subgroup this port belongs to
            const subgroupOffset = Math.floor((portIndex - 1) / splitSize) * splitSize + 1;
            const subgroupKey = `${groupId}:${subgroupOffset}`;
            if (typeof layout[subgroupKey] === 'object' && layout[subgroupKey].folded) {
                return true;
            }
        }
    }
    
    // Check if any subgroup is folded (for bundling detection without specific port)
    for (const key of Object.keys(layout)) {
        if (key.startsWith(groupId + ':') && typeof layout[key] === 'object' && layout[key].folded) {
            return true;
        }
    }
    
    return false;
}

/**
 * Set the side for a group in the layout modal
 * @param {string} groupId - Group identifier (index)
 * @param {string} side - Side ('left', 'right', 'top', 'bottom')
 * @param {string} defaultSide - Default side for this group
 */
function setGroupSide(groupId, side, defaultSide) {
    // groupId is now always the string index ("0", "1", etc.)
    // Read existing override - check both index key and any potential legacy key
    const existing = layoutOverrides[groupId] || {};
    const existingObj = (typeof existing === 'string') ? { side: existing } : existing;
    const currentSplit = existingObj.splitSize || null;
    const currentColor = existingObj.color || null;
    
    if (side === defaultSide && !currentSplit && !currentColor) {
        // If setting to default and no other overrides, remove entirely
        delete layoutOverrides[groupId];
    } else if (side === defaultSide) {
        // Default side but has other overrides - keep them without side
        const newOverride = {};
        if (currentSplit) newOverride.splitSize = currentSplit;
        if (currentColor) newOverride.color = currentColor;
        layoutOverrides[groupId] = Object.keys(newOverride).length > 0 ? newOverride : undefined;
        if (!layoutOverrides[groupId]) delete layoutOverrides[groupId];
    } else {
        // Non-default side
        const newOverride = { side: side };
        if (currentSplit) newOverride.splitSize = currentSplit;
        if (currentColor) newOverride.color = currentColor;
        layoutOverrides[groupId] = newOverride;
    }
    
    // Re-render to update button states
    const device = instanceModel.getById(layoutInstanceId);
    if (device) {
        const template = getTemplateForDeviceFn(device);
        if (template) {
            renderLayoutGroups(template);
        }
    }
}

/**
 * Set the split size for a group
 * @param {string} groupId - Group identifier (index)
 * @param {string} value - Split size value as string
 */
function setGroupSplit(groupId, value) {
    // groupId is now always the string index ("0", "1", etc.)
    const existing = layoutOverrides[groupId] || {};
    const existingObj = (typeof existing === 'string') ? { side: existing } : existing;
    const currentSide = existingObj.side || null;
    const currentColor = existingObj.color || null;
    const splitSize = parseInt(value);
    
    if (!splitSize || splitSize < 1) {
        // Removing split - keep other overrides if present
        const newOverride = {};
        if (currentSide) newOverride.side = currentSide;
        if (currentColor) newOverride.color = currentColor;
        if (Object.keys(newOverride).length > 0) {
            layoutOverrides[groupId] = newOverride;
        } else {
            delete layoutOverrides[groupId];
        }
    } else {
        // Setting split - preserve other overrides
        const newOverride = { splitSize: splitSize };
        if (currentSide) newOverride.side = currentSide;
        if (currentColor) newOverride.color = currentColor;
        layoutOverrides[groupId] = newOverride;
    }
    
    // Re-render to update preview
    const device = instanceModel.getById(layoutInstanceId);
    if (device) {
        const template = getTemplateForDeviceFn(device);
        if (template) {
            renderLayoutGroups(template);
        }
    }
}

/**
 * Close the layout modal
 */
function closeLayoutModal() {
    layoutModal.classList.remove('active');
    layoutInstanceId = null;
    layoutOverrides = {};
    layoutPortOrder = 'default';
    layoutDisplayName = '';
    // Reset modal title
    const modalTitle = layoutModal.querySelector('h2');
    modalTitle.textContent = 'Instance Layout';
}

/**
 * Reset instance layout to defaults
 */
function resetInstanceLayout() {
    layoutOverrides = {};
    layoutPortOrder = 'default';
    layoutDisplayName = '';
    
    // Reset display name input
    const displayNameInput = document.getElementById('layout-display-name');
    if (displayNameInput) {
        displayNameInput.value = '';
    }
    
    // Reset clockwise toggle
    const clockwiseToggle = document.getElementById('layout-clockwise-toggle');
    if (clockwiseToggle) {
        clockwiseToggle.checked = false;
    }
    
    const device = instanceModel.getById(layoutInstanceId);
    if (device) {
        const template = getTemplateForDeviceFn(device);
        if (template) {
            renderLayoutGroups(template);
        }
    }
    showToast('Layout reset to defaults');
}

/**
 * Save the instance layout
 */
function saveInstanceLayout() {
    const device = instanceModel.getById(layoutInstanceId);
    if (!device) {
        showToast('Device not found', true);
        closeLayoutModal();
        return;
    }
    
    // Read display name from input
    const displayNameInput = document.getElementById('layout-display-name');
    const newDisplayName = displayNameInput ? displayNameInput.value.trim() : '';
    
    // Capture state before changes for undo
    const deviceId = device.id;
    const oldLayout = device.groupLayout ? deepClone(device.groupLayout) : null;
    const oldPortOrder = device.portOrder || null;
    const oldDisplayName = device.displayName || null;
    
    // Save layout overrides to the instance
    if (Object.keys(layoutOverrides).length > 0) {
        device.groupLayout = {...layoutOverrides};
    } else {
        // Remove groupLayout if empty (using defaults)
        delete device.groupLayout;
    }
    
    // Save port order setting
    if (layoutPortOrder === 'clockwise') {
        device.portOrder = 'clockwise';
    } else {
        delete device.portOrder;
    }
    
    // Save display name
    if (newDisplayName) {
        device.displayName = newDisplayName;
    } else {
        delete device.displayName;
    }
    
    const newLayout = device.groupLayout ? deepClone(device.groupLayout) : null;
    const newPortOrder = device.portOrder || null;
    const savedDisplayName = device.displayName || null;
    
    // Re-render the device
    renderDeviceFn(device);
    renderAllCablesFn();
    updateConnectedPortsFn();
    scheduleAutosaveFn();
    
    // Get template for undo description
    const template = getTemplateForDeviceFn(device);
    const deviceName = template ? template.name : 'device';
    
    // Record action for undo/redo
    recordActionFn(
        'edit-layout',
        `Edit ${deviceName} layout`,
        // Undo: restore old layout
        () => {
            const d = instanceModel.getById(deviceId);
            if (d) {
                if (oldLayout) {
                    d.groupLayout = deepClone(oldLayout);
                } else {
                    delete d.groupLayout;
                }
                if (oldPortOrder) {
                    d.portOrder = oldPortOrder;
                } else {
                    delete d.portOrder;
                }
                if (oldDisplayName) {
                    d.displayName = oldDisplayName;
                } else {
                    delete d.displayName;
                }
                renderDeviceFn(d);
                renderAllCablesFn();
                updateConnectedPortsFn();
                scheduleAutosaveFn();
                eventBus.emit(EVENTS.DEVICE_UPDATED, { deviceId });
            }
        },
        // Redo: apply new layout
        () => {
            const d = instanceModel.getById(deviceId);
            if (d) {
                if (newLayout) {
                    d.groupLayout = deepClone(newLayout);
                } else {
                    delete d.groupLayout;
                }
                if (newPortOrder) {
                    d.portOrder = newPortOrder;
                } else {
                    delete d.portOrder;
                }
                if (savedDisplayName) {
                    d.displayName = savedDisplayName;
                } else {
                    delete d.displayName;
                }
                renderDeviceFn(d);
                renderAllCablesFn();
                updateConnectedPortsFn();
                scheduleAutosaveFn();
                eventBus.emit(EVENTS.DEVICE_UPDATED, { deviceId });
            }
        }
    );
    
    // Emit device updated event
    eventBus.emit(EVENTS.DEVICE_UPDATED, { deviceId });
    
    closeLayoutModal();
    showToast('Layout saved');
}
